import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { useRequestSubscriptionQuotaAddon } from '@/state/mutations/transactions/use-request-subscription-quota-addon';
import { useNotifications } from '@/hooks/use-notifications';
import { useSubscriptionCheckoutCartQuery } from '@/state/queries/transactions/use-subscription-checkout-cart';
import { usePatchOrganizationSubscriptionCheckoutCart } from '@/state/mutations/transactions/use-patch-organization-subscription-checkout-cart';
import type { SubscriptionBillingPeriod, SubscriptionTier } from '@/types/dtos/transactions/subscription-plan.dto';

function mergeQuotaAddons(
  existing: { quotaKey: string; quantity: number }[] | undefined,
  quotaKey: string,
  addQty: number,
): { quotaKey: string; quantity: number }[] {
  const list = [...(existing ?? [])];
  const idx = list.findIndex((l) => l.quotaKey === quotaKey);
  if (idx >= 0) {
    list[idx] = { quotaKey, quantity: list[idx].quantity + addQty };
  } else {
    list.push({ quotaKey, quantity: addQty });
  }
  return list;
}

export default function AddonQuotaDialog({
  open,
  onClose,
  organizationId,
  quotaKey,
  quotaLabel,
  canCompleteCheckout,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  quotaKey: string;
  quotaLabel: string;
  canCompleteCheckout: boolean;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const [quantity, setQuantity] = useState(1);
  const { mutateAsync, isPending: quotaAddonPending } = useRequestSubscriptionQuotaAddon();
  const patchCart = usePatchOrganizationSubscriptionCheckoutCart();
  const { data: cartResp } = useSubscriptionCheckoutCartQuery(open ? organizationId : undefined);

  const isPending = canCompleteCheckout ? quotaAddonPending : patchCart.isPending;

  const handleSubmit = async () => {
    if (quantity < 1) {
      return;
    }
    try {
      if (canCompleteCheckout) {
        const res = await mutateAsync({
          organizationId,
          quotaKey,
          quantity,
          redirectUrlAfterPayment: window.location.href,
        });
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl;
          return;
        }
        notification.notifySuccess(t('subscription.organization.planPage.addonSuccess'));
        onClose();
        return;
      }

      const planRow = cartResp?.cart?.lines?.[0];

      await patchCart.mutateAsync({
        organizationId,
        body: {
          ...(planRow?.tier && planRow.subscriptionProductKey
            ? {
                lines: [
                  {
                    subscriptionProductKey: planRow.subscriptionProductKey,
                    tier: planRow.tier as SubscriptionTier,
                    billingPeriod: (planRow.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod,
                    additionalQuotaUnits: planRow.additionalQuotaUnits,
                  },
                ],
              }
            : {}),
          quotaAddons: mergeQuotaAddons(cartResp?.cart?.quotaAddons, quotaKey, quantity),
        },
      });
      notification.notifySuccess(t('subscription.organization.planPage.cartSavedToast'));
      onClose();
    } catch {
      notification.notifyError(t('subscription.organization.planPage.addonError'));
    }
  };

  return (
    <Dialog key={`${quotaKey}-${open}`} open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('subscription.organization.planPage.addonDialogTitle', { label: quotaLabel })}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('subscription.organization.planPage.addonDialogHint')}
        </Typography>
        <TextField
          label={t('subscription.organization.planPage.quantity')}
          type="number"
          fullWidth
          slotProps={{ htmlInput: { min: 1, step: 1 } }}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common.proceed')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
